import React, { Component } from 'react';
import { View, Keyboard, SectionList } from 'react-native';
import Contacts from 'react-native-contacts';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import {
  forEach as _forEach,
  isFunction as _isFunction,
  orderBy as _orderBy,
  slice as _slice,
} from 'lodash';
import PropTypes from 'prop-types';

import { VFText } from '@vfgroup-oneplatform/foundation/Components';
import { LightThemeColors, withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import ContactItem from './Components/ContactItem';
import ContactsListLoading from './Components/ContactsListLoading';
import SearchBar from './Components/SearchBar';
import styles from './ContactList.Styles';
import { getAvatarInitials } from './Utils/AvatarInitials';
import { phoneNumberRegex } from './Utils/SearchRegex';

/*
 * order criteria is by
 * 1- normal names that start with alphabetical letters
 * 2- phone only without names
 * 3- names start with numbers
 */
const orderContactByNumbersAfterNames = contacts => {
  const orderedItems = _orderBy(
    contacts,
    [
      _contact => !/^(\s)*[a-zA-Z]/g.test(_contact.givenName),
      _contact => !/^(\s)*[0-9]+$/g.test(_contact.givenName),
      _contact => _contact.givenName?.trim()?.toLowerCase() ?? '',
      'phoneNumber',
    ],
    ['asc', 'asc', 'asc', 'asc'],
  );
  return orderedItems;
};
const contactsMapping = contacts => {
  const _contacts = [];
  _forEach(contacts, contact => {
    contact.phoneNumbers.forEach((number, index) => {
      _contacts.push({
        ...contact,
        recordID: `${contact.recordID}-#${index}`,
        phoneNumber: number,
      });
    });
  });
  return orderContactByNumbersAfterNames(_contacts);
};

class ContactList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showRecentSection: true,
      contacts: [],
      loading: true,
      isKeyboardVisible: false,
      keyboardHeight: 0,
    };
  }

  componentDidMount() {
    this.addKeyboardListeners();

    Contacts.checkPermission().then(permission => {
      if (permission === 'authorized') {
        this.loadContacts();
      }
    });
  }

  componentWillUnmount() {
    this.keyboardDidShowListener.remove();
    this.keyboardDidHideListener.remove();
  }

  _keyboardDidShow = e => {
    this.setState({
      isKeyboardVisible: true,
      keyboardHeight: e.endCoordinates.height,
    });
  };

  _keyboardDidHide = () => {
    this.setState({ isKeyboardVisible: false, keyboardHeight: 0 });
  };

  addKeyboardListeners = () => {
    this.keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', this._keyboardDidShow);
    this.keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', this._keyboardDidHide);
  };

  loadContacts() {
    Contacts.getAll()
      .then(contacts => {
        const _contacts = contactsMapping(contacts);
        this.setState({
          contacts: _contacts,
          loading: false,
        });
      })
      .catch(e => {
        this.setState({ loading: false });
      });
  }

  search = text => {
    if (!text) {
      this.loadContacts();
      this.setState({ showRecentSection: true });
    } else {
      this.setState({ showRecentSection: false });
      if (phoneNumberRegex.test(text)) {
        Contacts.getContactsByPhoneNumber(text).then(contacts => {
          const _contacts = contactsMapping(contacts);
          this.setState({ contacts: _contacts });
        });
      } else {
        Contacts.getContactsMatchingString(text).then(contacts => {
          const _contacts = contactsMapping(contacts);
          this.setState({ contacts: _contacts });
        });
      }
    }
  };

  pressContactAndRemoveListener = phoneNumber => {
    const { onPressContact } = this.props;
    this.keyboardDidHideOnPressContactListener.remove();
    _isFunction(onPressContact) && onPressContact(phoneNumber);
  };

  handleOnPressContact = phoneNumber => {
    const { onPressContact } = this.props;
    const { isKeyboardVisible } = this.state;
    if (isKeyboardVisible) {
      this.keyboardDidHideOnPressContactListener = Keyboard.addListener('keyboardDidHide', () =>
        this.pressContactAndRemoveListener(phoneNumber),
      );
      Keyboard.dismiss();
    } else {
      _isFunction(onPressContact) && onPressContact(phoneNumber);
    }
  };

  renderSectionTitle = title => {
    const { theme } = this.props;
    return (
      <View
        style={[
          styles.listTitleContainer(theme),
          title === 'all' && this.state.showRecentSection
            ? styles.listTitleContainerMargined
            : null,
        ]}
      >
        <VFText
          i18nKey={`top_up_someone_else_contact_list_section_${title}_title`}
          style={styles.listTitle(theme)}
        />
      </View>
    );
  };

  renderContactItem = ({ item, index }) => {
    const { givenName, familyName, name, phoneNumber, id } = item;
    const contactName = givenName ? `${givenName} ${familyName}` : name;
    const number = phoneNumber?.number ?? id;

    // Fix android issue in react-native-contacts Retrieves unnamed numbers with name = the number
    const title = contactName?.trim() == number ? null : contactName;
    return (
      <ContactItem
        avatarPlaceholder={getAvatarInitials(title)}
        title={title}
        description={`${item.company}`}
        phoneNumber={number}
        onPress={() => {
          this.handleOnPressContact(number);
        }}
      />
    );
  };

  render() {
    const { theme, containerStyle, recentContacts, maxNumberOfRecentContacts } = this.props;
    const { loading, contacts, showRecentSection } = this.state;

    const _recentContacts = _slice(recentContacts, 0, maxNumberOfRecentContacts);

    const sections = [
      ...(_recentContacts && !!_recentContacts?.length && showRecentSection
        ? [
            {
              title: 'recent',
              data: _recentContacts,
            },
          ]
        : []),
      {
        title: 'all',
        data: contacts,
      },
    ];

    return (
      <View style={[styles.container, containerStyle]}>
        {loading ? (
          <ContactsListLoading />
        ) : (
          <View style={styles.borderContainer(theme)}>
            <View>
              <SearchBar onChangeText={this.search} isLoading={loading} />
            </View>
            <SectionList
              sections={sections}
              stickySectionHeadersEnabled={false}
              nestedScrollEnabled
              keyExtractor={(item, index) => item + index}
              renderItem={this.renderContactItem}
              renderSectionHeader={({ section: { title } }) => this.renderSectionTitle(title)}
            />
          </View>
        )}
        <View style={{ height: this.state.keyboardHeight }} />
      </View>
    );
  }
}

ContactList.propTypes = {
  containerStyle: ViewPropTypes.style,
  recentContacts: PropTypes.array,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  onPressContact: PropTypes.func,
  maxNumberOfRecentContacts: PropTypes.number,
};

ContactList.defaultProps = {
  theme: LightThemeColors,
  maxNumberOfRecentContacts: 4,
  recentContacts: [],
};
export default withTheme(ContactList);
export { contactsMapping };
